import React, { useCallback, useId, useMemo, useState } from "react";

import { ClockIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { Controller, Control, FieldValues } from "react-hook-form";
import PhoneInput from "react-phone-input-2";

import "react-phone-input-2/lib/style.css";
import { Typography } from "../Typography/Typography";
// type ColorType = 'primary' | 'secondary' | 'error' | 'info' | 'success';
type Size = "small" | "medium" | "large";

const sizeClasses: { [S in Size]: string } = {
  small: "px-4 py-[0.4rem] placeholder:text-base text-md",
  medium: "px-2 py-3 placeholder:text-md text-md",
  large: "px-3 py-3.5 placeholder:text-lg text-lg",
};

type ControllerOnChangeType = (event: {
  target: { name: string; value: string | number | boolean };
}) => void;

type InputType =
  | "text"
  | "email"
  | "number"
  | "password"
  | "tel"
  | "date"
  | "time";
type MultilineProps =
  | {
      multiline?: true;
      rows: number;
    }
  | {
      multiline?: false;
      rows?: never;
    };

type ControlProps =
  | {
      control?: unknown;
      name: string;
    }
  | {
      control?: false | undefined;
      name?: never;
    };

type InputProps = {
  size?: Size;
  className?: string;
  /** Content shown inside the input at its leading edge. */
  startAdornment?: React.ReactNode;
  helperText?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: InputType;
  maxLength?: number;
  disabled?: boolean;
  error?: boolean;
  isFilterActive?: boolean;
  fullWidth?: boolean;
  disableHelperText?: boolean;
  defaultValue?: string;
  id?: string;
  label?: string;
  step?: string;
  required?: boolean;
  showCharacterLimit?: boolean;
  autoComplete?: string;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
} & MultilineProps &
  ControlProps;

function openNativePicker(event: React.MouseEvent<HTMLInputElement>) {
  const input = event.currentTarget;
  if (
    input.disabled ||
    input.readOnly ||
    typeof input.showPicker !== "function"
  ) {
    return;
  }

  try {
    input.showPicker();
  } catch {
    // Keep the browser's normal click behavior as the fallback.
  }
}

export function TextInput({
  type = "text",
  size = "medium",
  className,
  startAdornment,
  helperText,
  error,
  isFilterActive,
  placeholder,
  value,
  onChange,
  required,
  disabled,
  fullWidth,
  multiline,
  rows = 6,
  control,
  name,
  disableHelperText,
  defaultValue,
  id,
  maxLength,
  label,
  step,
  onBlur,
  onKeyDown,
  showCharacterLimit = false,
  autoComplete,
}: InputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const helperTextId = `${inputId}-helper-text`;
  const describedBy =
    !disableHelperText && helperText ? helperTextId : undefined;

  const classes = useMemo(
    () =>
      classNames(
        `w-full rounded-md border border-background-dark bg-background-light text-background-contrastText shadow-sm placeholder:text-zinc-400 transition-[border-color,box-shadow] duration-150 enabled:hover:border-primary-light focus-visible:border-primary-main focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-main ${sizeClasses[size]}`,
        className,
        {
          "opacity-60 cursor-not-allowed": disabled,
        },
        {
          "!border-error-main focus-visible:!ring-error-main": !!error,
          "!border-primary-main !bg-primary-main/5": !!isFilterActive,
        },
        {
          "!pl-10": !!startAdornment && !multiline && type !== "tel",
        },
      ),

    [className, disabled, error, size],
  );

  const getInput = useCallback(
    (
      controlChange?: ControllerOnChangeType,
      formValue?: FieldValues["value"],
      controlBlur?: () => void,
    ) =>
      multiline ? (
        <textarea
          id={inputId}
          name={name ?? ""}
          required={required}
          className={classes}
          placeholder={placeholder}
          value={control ? formValue : value}
          maxLength={maxLength}
          aria-describedby={describedBy}
          aria-invalid={error || undefined}
          onKeyDown={onKeyDown}
          onBlur={() => {
            controlBlur?.();
            onBlur?.();
          }}
          onChange={
            control
              ? controlChange
              : (e) => {
                  onChange?.(e.target.value);
                }
          }
          disabled={disabled}
          rows={rows}
          defaultValue={defaultValue}
          autoComplete={autoComplete}
        />
      ) : name === "phone" || type === "tel" ? (
        <PhoneInput
          inputProps={{
            name,
            className: classNames(classes, "pl-12 !z-5"),
            id: inputId,
            disabled,
            required,
            "aria-describedby": describedBy,
            "aria-invalid": error || undefined,
            autoComplete,
            onBlur: () => {
              controlBlur?.();
              onBlur?.();
            },
          }}
          value={control ? formValue || defaultValue : value || defaultValue}
          onChange={
            control
              ? (phone: string) =>
                  controlChange?.({
                    target: { value: phone, name: name || "" },
                  })
              : (phone: string) => {
                  onChange?.(phone);
                }
          }
          placeholder={placeholder}
          disabled={disabled}
          enableSearch
          disableSearchIcon
          searchClass="[&>input]:!border-none !px-0.5 w-full !pt-2 !pb-1 !mb-1 !border-b !border-b-zinc-400 !bg-background-light !text-background-contrastText !outline-none"
          containerClass="relative !rounded-md !bg-background-light !mb-2.5"
          dropdownClass="!absolute !w-full !p-2 !pt-0 [&>li[role]]:!rounded-sm [&>li[role]]:!p-2 [&>li[role]]:!text-background-contrastText [&>li[role]:hover]:!bg-primary-main [&>li[role]:hover>span]:!text-primary-contrastText [&>li[aria-selected]]:!bg-primary-main [&>li[aria-selected]>span]:!text-white !bg-background-light !text-background-contrastText !ml-0 !mt-11 !shadow-md"
          buttonClass={classNames(
            "!bg-transparent !flex !-mt-[37.5px] !ml-0.5 !border-2 !static !h-[30px] [&>div[role='button']]:!bg-background-light !border-none [&>div[role='button']]:!rounded-sm",
          )}
          searchPlaceholder="Search"
          country="in"
        />
      ) : (
        <div className="relative">
          {startAdornment && (
            <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-zinc-400">
              {startAdornment}
            </span>
          )}
          <input
            id={inputId}
            name={name ?? ""}
            maxLength={maxLength}
            type={type === "password" && isPasswordVisible ? "text" : type}
            required={required}
            className={classNames(classes, {
              "cursor-pointer !pr-10 [color-scheme:light] dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0":
                type === "time",
            })}
            placeholder={placeholder}
            value={control ? formValue : value}
            step={step}
            aria-describedby={describedBy}
            aria-invalid={error || undefined}
            onBlur={() => {
              controlBlur?.();
              onBlur?.();
            }}
            onClick={type === "time" ? openNativePicker : undefined}
            onChange={
              control
                ? controlChange
                : (e) => {
                    onChange?.(e.target.value);
                  }
            }
            disabled={disabled}
            defaultValue={defaultValue}
            autoComplete={autoComplete}
          />
          {type === "time" && (
            <ClockIcon
              aria-hidden="true"
              className={classNames(
                "pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2",
                error ? "text-error-main" : "text-primary-main",
              )}
            />
          )}
          {type === "password" &&
            (isPasswordVisible ? (
              <button
                type="button"
                aria-label="Hide password"
                aria-pressed="true"
                disabled={disabled}
                onClick={() => setIsPasswordVisible(false)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 transition-colors duration-150 hover:text-background-contrastText focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main disabled:cursor-not-allowed disabled:opacity-60"
              >
                <EyeIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                aria-label="Show password"
                aria-pressed="false"
                disabled={disabled}
                onClick={() => setIsPasswordVisible(true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 transition-colors duration-150 hover:text-background-contrastText focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main disabled:cursor-not-allowed disabled:opacity-60"
              >
                <EyeSlashIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            ))}
        </div>
      ),
    [
      multiline,
      inputId,
      name,
      classes,
      placeholder,
      control,
      value,
      onKeyDown,
      disabled,
      rows,
      defaultValue,
      type,
      maxLength,
      isPasswordVisible,
      step,
      onBlur,
      onChange,
      required,
      startAdornment,
      error,
      describedBy,
    ],
  );

  const renderFeedback = (currentValue?: string) => (
    <>
      {maxLength && showCharacterLimit && typeof currentValue === "string" && (
        <div className="flex justify-end">
          <Typography variant="body2">
            {maxLength - currentValue.length} characters left{" "}
          </Typography>
        </div>
      )}
      {!disableHelperText && (
        <p
          id={helperText ? helperTextId : undefined}
          className={classNames(
            "my-1 h-[0.8rem] text-left text-sm text-background-contrastText",
            {
              "!text-error-main": !!error,
            },
          )}
        >
          {helperText}
        </p>
      )}
    </>
  );

  return (
    <div
      className={classNames("relative w-60 ", {
        " w-full ": fullWidth,
      })}
    >
      {label && (
        <label htmlFor={inputId}>
          <Typography className="mb-1 opacity-80" variant="h5">
            {label}{" "}
            {required && <span className="mx-1 text-error-dark">*</span>}
          </Typography>
        </label>
      )}
      {control ? (
        <Controller
          name={name as string}
          control={control as Control}
          render={({
            field: {
              onBlur: controlBlur,
              onChange: controlChange,
              value: fieldValues,
            },
          }) => (
            <>
              {getInput(controlChange, fieldValues, controlBlur)}
              {renderFeedback(
                typeof fieldValues === "string" ? fieldValues : undefined,
              )}
            </>
          )}
        />
      ) : (
        <>
          {getInput()}
          {renderFeedback(value ?? defaultValue)}
        </>
      )}
    </div>
  );
}
